import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useCompanyGroupDetail } from '../hooks';
import { Company } from '../services/firebaseFunctions';
import { useAuth } from '../contexts/AuthContext';
import { companyGroupErrorMessage } from '../utils/adminFeedback';

const noPermissionTooltip = 'Você não tem permissão de edição no módulo Empresas.';

const CompanyGroupDetailPage: React.FC = () => {
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('empresas', 'editar');
  const {
    companyGroup,
    companies,
    eligibleCompanies,
    eligibleNextCursor,
    nextCursor,
    loading,
    error,
    loadMore,
    searchEligible,
    loadMoreEligible,
    link,
    unlink,
  } = useCompanyGroupDetail(id);
  const [eligibleId, setEligibleId] = useState('');
  const [eligibleSearch, setEligibleSearch] = useState('');
  const [feedback, setFeedback] = useState<string>();

  useEffect(() => {
    setEligibleId('');
  }, [companyGroup]);

  const changeLink = async (action: () => Promise<void>) => {
    setFeedback(undefined);
    try {
      await action();
    } catch (caught) {
      setFeedback(companyGroupErrorMessage(caught));
    }
  };

  const linkSelectedCompany = () => {
    const company = eligibleCompanies.find((item) => item.id === eligibleId);
    if (company) void changeLink(() => link(company));
  };

  if (loading && !companyGroup) return <CircularProgress />;
  if (!companyGroup) return <Alert severity="error">{error || 'Grupo não encontrado.'}</Alert>;

  return <Box>
    <Typography variant="h4">{companyGroup.name}</Typography>
    {companyGroup.description && <Typography sx={{ mb: 2 }}>{companyGroup.description}</Typography>}
    {(error || feedback) && <Alert severity="error" sx={{ my: 2 }}>{feedback || error}</Alert>}
    <Paper>
      <Table>
        <TableHead><TableRow><TableCell>Empresa vinculada</TableCell><TableCell>CNPJ</TableCell><TableCell /></TableRow></TableHead>
        <TableBody>
          {companies.length === 0 ? <TableRow><TableCell colSpan={3}>Nenhuma Empresa vinculada.</TableCell></TableRow>
            : companies.map((company: Company) => <TableRow key={company.id}>
              <TableCell>{company.name}</TableCell><TableCell>{company.cnpj}</TableCell>
              <TableCell>
                <Tooltip title={canManage ? '' : noPermissionTooltip}>
                  <span>
                    <Button color="error" disabled={!canManage} onClick={() => void changeLink(() => unlink(company))}>Desvincular</Button>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>)}
        </TableBody>
      </Table>
    </Paper>
    {nextCursor && <Button onClick={() => void loadMore()} sx={{ mt: 2 }}>Carregar mais</Button>}
    <Typography variant="h6" sx={{ mt: 4 }}>Vincular Empresa</Typography>
    <TextField size="small" label="Buscar Empresa" value={eligibleSearch} onChange={(event) => { const value = event.target.value; setEligibleSearch(value); void searchEligible(value); }} sx={{ mr: 1 }} />
    <Select value={eligibleId} displayEmpty inputProps={{ 'aria-label': 'Empresa a vincular' }} onChange={(event) => setEligibleId(event.target.value)}>
      <MenuItem value="">Selecione uma Empresa</MenuItem>
      {eligibleCompanies.map((company) => <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>)}
    </Select>
    {eligibleNextCursor && <Button onClick={() => void loadMoreEligible()}>Mais resultados</Button>}
    <Tooltip title={canManage ? '' : noPermissionTooltip}>
      <span>
        <Button sx={{ ml: 1 }} variant="contained" disabled={!canManage || !eligibleId} onClick={linkSelectedCompany}>Vincular</Button>
      </span>
    </Tooltip>
  </Box>;
};

export default CompanyGroupDetailPage;
